/**
 * features/hr/LoansHub.tsx
 * ------------------------------------------------------------------
 * One nav home for staff loans, with a sub-tab per loan type and a
 * dedicated sub-tab per bank scheme (Stanbic, BBS, …, plus any banks
 * the bursar has added). Each sub-tab is the shared DeductionEntry
 * screen — enter the loan (singly or by CSV) and it feeds payroll
 * automatically over its repayment window.
 * ------------------------------------------------------------------
 */
import { useMemo, useState } from 'react';
import { useHrData } from '@/features/hr/useHrData';
import DeductionEntry from '@/features/hr/DeductionEntry';
import { DEDUCTION_TYPES } from '@/lib/hrPayroll';
import type { DeductionType } from '@/lib/hrPayroll';
import { PageHeader } from '@/components/ui/primitives';

type Tab = { key: string; label: string; type: DeductionType; bank?: string };

export default function LoansHub({
  onOpenStaff,
}: {
  onOpenStaff?: (id: string) => void;
}) {
  const data = useHrData();

  const banks = useMemo(() => {
    const field = DEDUCTION_TYPES.bank_scheme.fields.find((f) => f.key === 'bankName');
    const extra = data.payrollSettings?.dropdowns?.bankName ?? [];
    const seen = new Set<string>();
    const out: string[] = [];
    for (const b of [...(field?.options ?? []), ...extra]) {
      const v = String(b).trim();
      if (v && !seen.has(v)) {
        seen.add(v);
        out.push(v);
      }
    }
    return out;
  }, [data.payrollSettings]);

  const tabs = useMemo<Tab[]>(
    () => [
      { key: 'salary_advance', label: 'Salary Advance', type: 'salary_advance' },
      { key: 'school_loan', label: 'School Loan', type: 'school_loan' },
      ...banks.map((b) => ({ key: `bank:${b}`, label: b, type: 'bank_scheme' as DeductionType, bank: b })),
    ],
    [banks],
  );

  const [activeKey, setActiveKey] = useState('salary_advance');
  const active = tabs.find((t) => t.key === activeKey) ?? tabs[0];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Loans & Advances"
        subtitle="Grant and track staff loans, salary advances and bank schemes. Each one is deducted automatically on every pay run over its repayment window and recovered at exit."
      />

      <div className="flex flex-wrap gap-2 border-b border-gray-200 dark:border-gray-700">
        {tabs.map((t) => {
          const on = t.key === active.key;
          return (
            <button
              key={t.key}
              onClick={() => setActiveKey(t.key)}
              className={`px-4 py-2 text-sm font-semibold rounded-t-lg -mb-px border-b-2 transition-colors ${
                on
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium'
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      <DeductionEntry
        key={active.key}
        type={active.type}
        bank={active.bank}
        onOpenStaff={onOpenStaff}
      />
    </div>
  );
}
